import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import {
  getTc6BestAttempt,
  getTc6Attempts,
  recalculateTc6Payload,
  type Tc6AttemptPayload,
  type Tc6ChartImages,
  type Tc6ResultPayload,
} from '../tc6/tc6Domain';
import {
  TC6DistanceChartSurface,
  TC6VitalsChartSurface,
} from '../renderers/TC6ResultRenderer';

const waitForNextPaint = async (frames = 3): Promise<void> => {
  for (let index = 0; index < frames; index += 1) {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }
};

const renderElementToCanvas = async (element: HTMLElement) => {
  const html2canvas = (await import('html2canvas')).default;
  return html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    logging: false,
    useCORS: true,
    allowTaint: true,
  });
};

type TC6ChartCaptureOptions = {
  comparisonPayload?: Tc6ResultPayload | null;
};

const resolveComparisonAttempt = (
  comparisonPayload: Tc6ResultPayload | null | undefined,
  attemptIndex: 0 | 1,
): Tc6AttemptPayload | null => {
  if (!comparisonPayload) return null;

  const directAttempt = attemptIndex === 1 ? comparisonPayload.attempt2 : comparisonPayload.attempt1;
  if (directAttempt) return directAttempt;

  const fallback = getTc6BestAttempt(comparisonPayload);
  return fallback.attempt ?? null;
};

function TC6ChartCaptureStage({
  payload,
  comparisonPayload = null,
}: {
  payload: Tc6ResultPayload;
  comparisonPayload?: Tc6ResultPayload | null;
}) {
  const attempts = getTc6Attempts(payload);

  return (
    <div className="flex flex-col gap-6 bg-white p-4">
      {attempts.map((attempt, index) => {
        const attemptKey = index === 0 ? 'attempt1' : 'attempt2';
        const comparisonAttempt = resolveComparisonAttempt(comparisonPayload, index === 0 ? 0 : 1);
        return (
          <div key={attemptKey} className="flex flex-col gap-4">
            <div data-chart-key={`${attemptKey}_distance_payload`} className="w-[640px]">
              <TC6DistanceChartSurface
                attempt={attempt}
                referenceKey={payload.referenceEquation}
                comparisonAttempt={comparisonAttempt}
                disableAnimation
              />
            </div>
            <div data-chart-key={`${attemptKey}_vitals_payload`} className="w-[640px]">
              <TC6VitalsChartSurface attempt={attempt} comparisonAttempt={comparisonAttempt} disableAnimation />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export class TC6ChartCaptureService {
  static async captureFromPayload(
    payload: Tc6ResultPayload | null | undefined,
    options?: TC6ChartCaptureOptions,
  ): Promise<Tc6ChartImages> {
    if (!payload) return {};

    const normalizedPayload = recalculateTc6Payload(payload);
    const comparisonPayload = options?.comparisonPayload ? recalculateTc6Payload(options.comparisonPayload) : null;
    const attempts = getTc6Attempts(normalizedPayload);
    if (!attempts.length || typeof document === 'undefined') return {};

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.position = 'fixed';
    host.style.left = '-10000px';
    host.style.top = '0';
    host.style.width = '720px';
    host.style.pointerEvents = 'none';
    host.style.opacity = '0';
    host.style.zIndex = '-1';
    host.style.background = '#ffffff';
    document.body.appendChild(host);

    let root: Root | null = null;

    try {
      root = createRoot(host);
      root.render(<TC6ChartCaptureStage payload={normalizedPayload} comparisonPayload={comparisonPayload} />);

      await waitForNextPaint(4);
      await new Promise((resolve) => window.setTimeout(resolve, 180));

      const chartNodes = Array.from(host.querySelectorAll<HTMLElement>('[data-chart-key]'));
      const capturedEntries = await Promise.all(
        chartNodes.map(async (node) => {
          const chartKey = node.dataset.chartKey;
          if (!chartKey) return null;

          const canvas = await renderElementToCanvas(node);
          return [chartKey, canvas.toDataURL('image/png')] as const;
        }),
      );

      return capturedEntries.reduce<Tc6ChartImages>((accumulator, entry) => {
        if (!entry) return accumulator;
        accumulator[entry[0] as keyof Tc6ChartImages] = entry[1];
        return accumulator;
      }, {});
    } finally {
      if (root) {
        root.unmount();
      }
      document.body.removeChild(host);
    }
  }
}
